import React from 'react';
import { Callout, List, P, Steps, Sub, Table, UI } from '../ui';

export const WebVersion: React.FC = () => (
  <>
    <P>
      브라우저로 접속해도 대화 내용과 기능은 동일합니다. 설치 없이 어디서나 쓸 수 있는 대신, PC 앱에만 있는 편의
      기능 몇 가지가 빠집니다.
    </P>

    <Sub>PC 앱과 웹 버전 비교</Sub>
    <Table
      head={['기능', 'PC 앱', '웹 브라우저']}
      rows={[
        ['대화·파일·통화', '동일', '동일'],
        ['트레이 아이콘', '있음', '없음'],
        ['PC 시작 시 자동 실행', '있음', '없음'],
        ['로그아웃 후에도 알림', '있음', '있음 (로그아웃 시 선택, 알림 허용 필요)'],
        [
          '알림 방식',
          'Windows 알림 + 작업표시줄 깜빡임 · 안 읽은 개수',
          '브라우저 알림 + 탭 제목 깜빡임',
        ],
        ['앱 업데이트', '자동 설치 후 재시작', '새로고침 안내 배너'],
        ['이미지 저장', '"다른 이름으로 저장" 창', '기본 다운로드 폴더'],
        ['일반 파일 내려받기', '기본 브라우저가 열림', '현재 탭에서 바로 내려받기'],
      ]}
    />

    <Sub>브라우저 알림 허용하기</Sub>
    <P>
      웹 버전에서 알림을 받으려면 처음 접속할 때 뜨는 알림 권한 요청을 <b>허용</b>해야 합니다. 아직 허용하지
      않았다면 화면 아래쪽에 <UI>알림 허용 안내</UI> 배너가 표시되며, <UI>알림 켜기</UI> 버튼을 눌러 허용할 수
      있습니다. 실수로 차단했다면 주소창 왼쪽의 자물쇠 아이콘을 클릭해 알림을 허용으로 바꾸세요. 차단된 상태라면
      앱 위쪽에 안내 배너가 표시됩니다.
    </P>
    <P>
      알림이 오면 브라우저 탭 제목이 <UI>[새 메시지] 대화방 이름</UI>과 원래 제목을 오가며 깜빡입니다. 창을 클릭하면
      멈춥니다.
    </P>

    <Sub anchor="web-mobile">휴대폰에서 쓰기</Sub>
    <P>
      휴대폰 브라우저로 접속하면 전용 모바일 화면으로 바뀝니다. 화면 아래의 <UI>홈</UI>·<UI>대화방</UI> 탭으로
      이동합니다.
    </P>
    <List>
      <>
        <b>홈</b> — <b>확인할 것</b>(안 읽은 메시지·알림함 개수), 최근 대화, 일정, 공지 대화방의 최신 소식,
        즐겨찾기, 지금 대화할 수 있는 동료, 기능 팁이 한 화면에 모입니다. 항목을 누르면 해당 대화방이 바로
        열리고, 확인할 것의 안 읽은 메시지 줄을 누르면 대화방 탭이 안 읽은 대화만 걸러 열립니다. 즐겨찾기의
        <UI>추가</UI>를 누르면 대화방을 골라 바로 올릴 수 있고, 대화방을 길게 눌러 <UI>채팅방 고정</UI>을 해도
        즐겨찾기에 표시됩니다.
      </>
      <>
        <b>대화방</b> — 전체 대화방 목록입니다. 대화방을 열면 전체 화면으로 전환되고, 왼쪽 위 <UI>〈</UI> 버튼으로
        목록에 돌아옵니다. 탭의 빨간 숫자는 읽지 않은 메시지 합계입니다.
      </>
      <>메시지를 길게 눌러 답장·복사·삭제 메뉴를 사용합니다.</>
      <>
        키보드의 줄바꿈(Enter) 키는 <b>줄만 바꿉니다</b>. 메시지 전송은 입력창 오른쪽의 종이비행기 버튼으로 합니다.
      </>
    </List>
    <Callout tone="tip" title="홈 화면에 추가하면 알림이 안정적입니다">
      <p>
        브라우저 탭으로만 열어두면 백그라운드에서 알림이 잘 오지 않을 수 있습니다. 화면에 표시되는{' '}
        <UI>홈 화면에 추가</UI> 안내를 따라 설치하면 앱처럼 실행되고 알림도 안정적으로 받습니다.
      </p>
    </Callout>
    <Steps>
      <>
        <b>iPhone</b> — 사파리 아래쪽 공유 버튼을 누르고 <UI>홈 화면에 추가</UI>를 선택합니다.
      </>
      <>
        <b>Android</b> — 화면에 나타나는 <UI>홈 화면에 추가</UI> 버튼을 누르거나, 크롬 메뉴에서 같은 항목을
        선택합니다.
      </>
      <>홈 화면에 생긴 아이콘으로 실행합니다.</>
    </Steps>
    <P>안내 배너를 닫으면 7일 동안 다시 표시되지 않습니다. 배너를 닫았더라도{' '}
      <UI>설정</UI> → <UI>알림 설정</UI>에서 현재 상태를 확인하고 알림을 켤 수 있습니다.</P>
    <Callout tone="info" title="아이폰은 세 가지가 모두 필요합니다">
      <p>
        아이폰에서 알림을 받으려면 <b>iOS 16.4 이상</b>, <b>홈 화면에 추가</b>, <b>알림 허용</b> 세 가지가 모두
        갖춰져야 합니다. 하나라도 빠지면 알림이 오지 않으며, 사파리 탭으로만 열어 둔 상태에서는 알림을 켤 수조차
        없습니다. 현재 무엇이 빠졌는지는 <UI>설정</UI> → <UI>알림 설정</UI>에서 확인할 수 있습니다.
      </p>
    </Callout>

    <Sub>웹 버전에서 알아둘 점</Sub>
    <List>
      <>
        <b>영상 통화는 https:// 주소로 접속했을 때만</b> 사용할 수 있습니다.
      </>
      <>휴대폰 브라우저에서는 화면 공유 버튼이 표시되지 않습니다.</>
      <>탭을 백그라운드로 3초 이상 두면 오프라인으로 전환되어, 알림이 푸시 방식으로 바뀝니다.</>
      <>브라우저의 시크릿 모드에서는 로그인 정보가 저장되지 않아 매번 다시 로그인해야 합니다.</>
    </List>
  </>
);
